import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TruckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useVehicles } from '@/hooks/useVehicles'
import { useLprEvents } from '@/hooks/useLprEvents'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { Vehicle, ParkingPermitSummary, LprEvent } from '@/types'

const PERMIT_TYPE_BADGE: Record<string, string> = {
  tenant_included: 'status-badge-success',
  tenant_addon: 'status-badge-info',
  monthly_external: 'status-badge-warning',
  daily_external: 'status-badge-ghost',
}

function formatCurrency(amount: number) {
  // LIFF-M5: fixed th-TH formatting with 2 decimals — bare toLocaleString()
  // follows the browser locale and drops decimals.
  return `฿${new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(amount)}`
}

// ─── Permit Card ─────────────────────────────────────────────────────────────

interface PermitCardProps {
  permit: ParkingPermitSummary
}

function PermitCard({ permit }: PermitCardProps) {
  const { t } = useTranslation('vehicles')
  const { formatDate } = useDateFormatter()

  const badgeClass = PERMIT_TYPE_BADGE[permit.permit_type] ?? 'status-badge-ghost'
  const permitTypeLabel = t(`permit_types.${permit.permit_type}`)

  return (
    <div className="bg-base-200 rounded-lg p-3 space-y-1 text-sm">
      <div className="flex items-center gap-2 mb-2">
        <span className={badgeClass}>{permitTypeLabel}</span>
      </div>

      {permit.zone_name && (
        <div className="flex justify-between">
          <span className="text-base-content/60">{t('labels.zone')}</span>
          <span className="font-medium">{permit.zone_name}</span>
        </div>
      )}

      {permit.slot_number && (
        <div className="flex justify-between">
          <span className="text-base-content/60">{t('labels.slot')}</span>
          <span className="font-medium font-mono">{permit.slot_number}</span>
        </div>
      )}

      {permit.sticker_number && (
        <div className="flex justify-between">
          <span className="text-base-content/60">{t('labels.sticker')}</span>
          <span className="font-medium font-mono">{permit.sticker_number}</span>
        </div>
      )}

      {permit.monthly_fee !== null && permit.monthly_fee !== undefined && (
        <div className="flex justify-between">
          <span className="text-base-content/60">{t('labels.monthly_fee')}</span>
          <span className="font-medium">{formatCurrency(permit.monthly_fee)}</span>
        </div>
      )}

      {permit.daily_fee !== null && permit.daily_fee !== undefined && (
        <div className="flex justify-between">
          <span className="text-base-content/60">{t('labels.daily_fee')}</span>
          <span className="font-medium">{formatCurrency(permit.daily_fee)}</span>
        </div>
      )}

      {permit.ended_at && (
        <div className="flex justify-between">
          <span className="text-base-content/60">{t('labels.valid_until')}</span>
          <span className="font-medium">{formatDate(permit.ended_at)}</span>
        </div>
      )}
    </div>
  )
}

// ─── LPR Event Row ────────────────────────────────────────────────────────────

interface LprEventRowProps {
  event: LprEvent
}

function LprEventRow({ event }: LprEventRowProps) {
  const { t } = useTranslation('vehicles')
  const { formatDate } = useDateFormatter()

  const directionLabel =
    event.direction === 'in'
      ? t('lpr.direction_in')
      : event.direction === 'out'
        ? t('lpr.direction_out')
        : t('lpr.direction_unknown')

  const directionBadge =
    event.direction === 'in'
      ? 'status-badge-success'
      : event.direction === 'out'
        ? 'status-badge-ghost'
        : 'status-badge-ghost'

  // Status enum is the Rails LprEvent contract: pending | verified_success |
  // verified_failed (api/app/models/lpr_event.rb). Anything else → pending.
  const statusBadge =
    event.verification_status === 'verified_success'
      ? 'status-badge-success'
      : event.verification_status === 'verified_failed'
        ? 'status-badge-error'
        : 'status-badge-warning'

  const statusLabel =
    event.verification_status === 'verified_success'
      ? t('lpr.status.verified_success')
      : event.verification_status === 'verified_failed'
        ? t('lpr.status.verified_failed')
        : t('lpr.status.pending')

  return (
    <div className="flex items-center gap-3 py-2 border-b border-base-200 last:border-0 text-sm">
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono font-medium text-base-content">
            {event.plate_text ?? '—'}
          </span>
          <span className={directionBadge}>{directionLabel}</span>
        </div>
        <p className="text-xs text-base-content/60">{formatDate(event.captured_at)}</p>
      </div>
      <span className={statusBadge} aria-label={statusLabel}>
        {statusLabel}
      </span>
    </div>
  )
}

// ─── Vehicle Card ─────────────────────────────────────────────────────────────

interface VehicleCardProps {
  vehicle: Vehicle
  /** Most recent LPR event for this vehicle, or undefined if LPR not gated. */
  lastEvent?: LprEvent
  /** Whether the LPR gate is active (parking_fee fallback via ≥1 event). */
  lprGated: boolean
}

function VehicleCard({ vehicle, lastEvent, lprGated }: VehicleCardProps) {
  const { t } = useTranslation('vehicles')
  const { formatDate } = useDateFormatter()

  const vehicleTypeLabel = t(`vehicle_types.${vehicle.vehicle_type}`)
  const brandModel = [vehicle.brand, vehicle.model].filter(Boolean).join(' ')

  return (
    <div className="flat-card p-4 space-y-3">
      {/* Vehicle header */}
      <div className="flex items-start gap-3">
        <div className="icon-container shrink-0" aria-label={vehicleTypeLabel}>
          <TruckIcon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xl font-bold font-mono tracking-wider text-base-content">
            {vehicle.license_plate}
          </p>
          <span className="status-badge-ghost mt-1 inline-block">{vehicleTypeLabel}</span>
        </div>
      </div>

      {/* Vehicle details */}
      <div className="space-y-1 text-sm">
        {brandModel && (
          <div className="flex justify-between">
            <span className="text-base-content/60">{t('labels.brand_model')}</span>
            <span className="font-medium text-right">{brandModel}</span>
          </div>
        )}

        {vehicle.color && (
          <div className="flex justify-between">
            <span className="text-base-content/60">{t('labels.color')}</span>
            <span className="font-medium">{vehicle.color}</span>
          </div>
        )}

        {/* Last seen — shown only when LPR gate is on */}
        {lprGated && lastEvent && (
          <div className="flex justify-between">
            <span className="text-base-content/60">{t('labels.last_seen')}</span>
            <span className="font-medium text-right">{formatDate(lastEvent.captured_at)}</span>
          </div>
        )}
      </div>

      {/* Permits section */}
      <div>
        <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wide mb-2">
          {t('labels.active_permits')}
        </p>
        {vehicle.active_permits.length > 0 ? (
          <div className="space-y-2">
            {vehicle.active_permits.map((permit) => (
              <PermitCard key={permit.id} permit={permit} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-base-content/40 italic">{t('labels.no_active_permits')}</p>
        )}
      </div>
    </div>
  )
}

// ─── Recent LPR Events section ─────────────────────────────────────────────────

const RECENT_LPR_LIMIT = 20

interface RecentLprEventsSectionProps {
  events: LprEvent[]
  totalCount: number
}

function RecentLprEventsSection({ events, totalCount }: RecentLprEventsSectionProps) {
  const { t } = useTranslation('vehicles')
  const [open, setOpen] = useState(false)

  const remainingCount = totalCount - events.length

  return (
    <div className="flat-card">
      {/* Collapsible toggle row */}
      <button
        type="button"
        className="w-full flex items-center justify-between p-4 text-sm font-semibold text-base-content"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls="lpr-events-body"
      >
        <span>{t('lpr.recent_events_title')}</span>
        {open ? (
          <ChevronUpIcon className="w-4 h-4 text-base-content/50" aria-hidden="true" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-base-content/50" aria-hidden="true" />
        )}
      </button>

      {/* Collapsible body */}
      <div id="lpr-events-body" className={open ? '' : 'hidden'}>
        <div className="px-4 pb-2">
          {events.length === 0 ? (
            <p className="text-sm text-base-content/40 italic py-2">{t('lpr.no_events')}</p>
          ) : (
            <>
              {events.map((event) => (
                <LprEventRow key={event.id} event={event} />
              ))}

              {/* "N more" footer linking to /vehicles/events (P3-09 route) */}
              {remainingCount > 0 && (
                <div className="pt-2 pb-1">
                  <Link
                    to="/vehicles/events"
                    className="text-xs text-primary font-semibold"
                    aria-label={t('lpr.view_all')}
                  >
                    {t('lpr.n_more', { count: remainingCount })}
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function VehiclesPage() {
  const { t } = useTranslation('vehicles')
  const { t: tc } = useTranslation('common')
  const { data: vehicles, isLoading, error } = useVehicles()

  // Gating strategy:
  // `parking_fee_enabled` is an account.settings key NOT exposed to LIFF
  // (not in /api/liff/resolve or /api/customer/account_info).
  // Fallback gate: if the customer has ≥1 LPR event the property has cameras
  // and LPR features are active. We always fetch with per_page=RECENT_LPR_LIMIT;
  // a non-empty result gates all LPR UI in one round-trip.
  const {
    data: lprData,
    isLoading: lprLoading,
  } = useLprEvents({ perPage: RECENT_LPR_LIMIT })

  const lprEvents: LprEvent[] = lprData?.data ?? []
  const lprTotal: number = lprData?.meta?.count ?? lprEvents.length

  // Gate is ON only when the API returned at least one LPR event.
  // While loading, gate is treated as OFF (no flash of LPR content).
  const lprGated = !lprLoading && lprEvents.length > 0

  // Build a per-vehicle index: vehicle_id → most recent event (already DESC ordered).
  const lastEventByVehicleId = new Map<number, LprEvent>()
  for (const event of lprEvents) {
    if (!lastEventByVehicleId.has(event.vehicle_id)) {
      lastEventByVehicleId.set(event.vehicle_id, event)
    }
  }

  return (
    <div className="p-4 space-y-3">
      {isLoading && <LoadingSpinner />}

      {error && (
        <div className="alert alert-error">{tc('errors.something_went_wrong')}</div>
      )}

      {!isLoading && !error && vehicles?.length === 0 && (
        <div className="text-center py-12 text-base-content/50">
          <div className="flex justify-center mb-3">
            <div className="icon-container">
              <TruckIcon className="w-5 h-5" />
            </div>
          </div>
          <p className="font-medium">{t('no_vehicles')}</p>
          <p className="text-sm mt-1">{t('no_vehicles_sub')}</p>
        </div>
      )}

      {vehicles?.map((vehicle) => (
        <VehicleCard
          key={vehicle.id}
          vehicle={vehicle}
          lastEvent={lprGated ? lastEventByVehicleId.get(vehicle.id) : undefined}
          lprGated={lprGated}
        />
      ))}

      {/* Recent LPR Events section — only when gate is ON */}
      {lprGated && (
        <RecentLprEventsSection events={lprEvents} totalCount={lprTotal} />
      )}
    </div>
  )
}
